import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Award, Send, Coins, Wallet, Flame, Trophy, Heart, Users, Sparkles, UserX, CalendarClock, PieChart as PieIcon, ArrowRight, Lightbulb } from "lucide-react";
import { useTranslation, useFormat } from "../i18n";
import analyticsService from "../services/analyticsService";
import { KpiTile, ChartCard, Segmented, Skeleton, ErrorState, PersonCell, DataTable, EmptyState, StatRow, RankList } from "../components/analytics/primitives";
import { TrendChart, DonutChart, ProfileRadar, SegmentBar } from "../components/analytics/charts";
import { SERIES } from "../components/analytics/palette";

const PRESETS = ["30d", "90d", "365d"];

/* ------------------------------------------------------------------------ */
/* Personal                                                                   */
/* ------------------------------------------------------------------------ */

const MyInsights = ({ preset }) => {
  const { t } = useTranslation("analytics");
  const fmt = useFormat();
  const q = useQuery({ queryKey: ["analytics", "me", preset], queryFn: () => analyticsService.getMyInsights(preset), staleTime: 60 * 1000 });
  if (q.isError) return <ErrorState error={q.error} onRetry={q.refetch} />;
  if (q.isLoading || !q.data) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} height={92} />
          ))}
        </div>
        <Skeleton height={260} />
      </div>
    );
  }
  const d = q.data;
  const s = d.summary;
  const pctFmt = (v) => (v === null || v === undefined ? "—" : fmt.number(v / 100, { style: "percent", maximumFractionDigits: 0 }));
  const nudgeParams = (n) => ({ ...n.params, days: n.params?.days ?? 0, balance: fmt.number(n.params?.balance || 0), share: n.params?.share ? pctFmt(n.params.share) : "" });

  return (
    <div className="space-y-4">
      {d.nudges.length > 0 && (
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {d.nudges.map((n) => (
            <li key={n.key} className="flex items-start gap-2.5 rounded-xl border border-purple-100 bg-purple-50/60 px-3 py-2.5 text-sm text-purple-900">
              <Lightbulb className="w-4 h-4 mt-0.5 text-purple-600 flex-shrink-0" aria-hidden="true" />
              <span className="flex-1">{t(`nudges.${n.key}`, nudgeParams(n))}</span>
              {n.key === "give_someone_recognition" && (
                <Link to="/recognition" className="inline-flex items-center gap-1 text-xs font-semibold text-purple-700 hover:underline flex-shrink-0">
                  {t("nudges.send_now")} <ArrowRight className="w-3 h-3" aria-hidden="true" />
                </Link>
              )}
              {n.key === "points_waiting" && (
                <Link to="/marketplace" className="inline-flex items-center gap-1 text-xs font-semibold text-purple-700 hover:underline flex-shrink-0">
                  {t("nudges.browse_rewards")} <ArrowRight className="w-3 h-3" aria-hidden="true" />
                </Link>
              )}
            </li>
          ))}
        </ul>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiTile icon={Award} label={t("me.received")} value={fmt.number(s.received.value)} metric={s.received} tone="purple" sub={s.last_received_at ? t("me.last", { when: fmt.relative(s.last_received_at) }) : t("me.none_yet")} />
        <KpiTile icon={Send} label={t("me.given")} value={fmt.number(s.given.value)} metric={s.given} tone="blue" sub={s.last_given_at ? t("me.last", { when: fmt.relative(s.last_given_at) }) : t("me.none_yet")} />
        <KpiTile icon={Coins} label={t("me.points_earned")} value={fmt.number(s.points_earned.value)} metric={s.points_earned} tone="green" sub={t("me.redeemed", { value: fmt.number(s.points_redeemed) })} />
        <KpiTile icon={Wallet} label={t("me.balance")} value={fmt.number(s.balance)} tone="yellow" sub={<Link to="/wallet" className="hover:underline">{t("me.view_wallet")}</Link>} />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiTile icon={Trophy} label={t("me.rank")} value={d.rank?.rank ? `#${fmt.number(d.rank.rank)}` : "—"} tone="pink" sub={d.rank ? (d.rank.percentile ? t("me.percentile", { value: pctFmt(d.rank.percentile), of: d.rank.of }) : t("me.no_rank")) : t("me.rank_hidden")} hint={t("me.rank_hint")} />
        <KpiTile icon={Flame} label={t("me.streak")} value={t("leaderboards.weeks_count", { count: d.streak.current })} tone="yellow" sub={t("me.best_streak", { count: d.streak.best })} />
        <KpiTile icon={Users} label={t("me.supporters")} value={fmt.number(s.unique_supporters)} tone="teal" sub={t("me.supporters_sub")} />
        <KpiTile icon={Heart} label={t("me.engagement")} value={fmt.number(s.reactions_received + s.comments_received)} tone="red" sub={t("me.engagement_sub", { reactions: s.reactions_received, comments: s.comments_received })} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <ChartCard title={t("me.trend_title")} subtitle={t("me.trend_subtitle")} icon={CalendarClock} className="lg:col-span-2">
          <TrendChart rows={d.trend} granularity="month" series={[{ key: "received", label: t("me.received"), color: SERIES[0], kind: "bar" }, { key: "given", label: t("me.given"), color: SERIES[3], kind: "bar" }]} />
        </ChartCard>
        <ChartCard title={t("me.profile_title")} subtitle={t("me.profile_subtitle")} icon={Sparkles}>
          {d.profile.length >= 3 ? <ProfileRadar data={d.profile} /> : <DonutChart data={d.profile} height={200} centerLabel={t("me.received")} />}
          {d.profile[0] && <p className="text-xs text-slate-600 mt-2 text-center">{t("me.signature", { type: d.profile[0].name, share: pctFmt(d.profile[0].share) })}</p>}
        </ChartCard>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <ChartCard title={t("me.by_type_title")} icon={PieIcon}>
          <DonutChart data={d.by_type} height={200} centerLabel={t("me.received")} />
        </ChartCard>
        <ChartCard title={t("me.supporters_title")} subtitle={t("me.supporters_subtitle")} icon={Heart}>
          {d.top_supporters.length === 0 ? (
            <EmptyState height={140} />
          ) : (
            <ul className="divide-y divide-slate-100">
              {d.top_supporters.map((r) => (
                <li key={r.user?.id} className="flex items-center gap-3 py-2">
                  <div className="flex-1 min-w-0">
                    <PersonCell user={r.user} />
                  </div>
                  <span className="text-sm font-semibold tabular-nums">{fmt.number(r.count)}</span>
                </li>
              ))}
            </ul>
          )}
        </ChartCard>
        <ChartCard title={t("me.recognised_title")} subtitle={t("me.recognised_subtitle")} icon={Send}>
          {d.top_recognised.length === 0 ? (
            <EmptyState height={140} />
          ) : (
            <ul className="divide-y divide-slate-100">
              {d.top_recognised.map((r) => (
                <li key={r.user?.id} className="flex items-center gap-3 py-2">
                  <div className="flex-1 min-w-0">
                    <PersonCell user={r.user} />
                  </div>
                  <span className="text-sm font-semibold tabular-nums">{fmt.number(r.count)}</span>
                </li>
              ))}
            </ul>
          )}
        </ChartCard>
      </div>

      {d.teams.length > 0 && (
        <ChartCard title={t("me.teams_title")} subtitle={t("me.teams_subtitle")} icon={Users}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
            {d.teams.map((tc) => (
              <div key={tc.team.id} className="py-1">
                <p className="text-sm font-medium text-slate-800">{tc.team.name}</p>
                <StatRow label={t("me.team_avg_received")} value={fmt.number(tc.avg_received, { maximumFractionDigits: 1 })} />
                <StatRow label={t("me.you_received")} value={fmt.number(s.received.value)} />
                <StatRow label={t("me.team_recognised_share")} value={pctFmt(tc.recognised_share)} />
              </div>
            ))}
          </div>
        </ChartCard>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------------ */
/* Team (for team admins / managers)                                          */
/* ------------------------------------------------------------------------ */

const TeamInsights = ({ teamId, preset }) => {
  const { t } = useTranslation("analytics");
  const fmt = useFormat();
  const q = useQuery({ queryKey: ["analytics", "team", teamId, preset], queryFn: () => analyticsService.getTeamInsights(teamId, { preset }), staleTime: 60 * 1000, enabled: Boolean(teamId) });
  if (q.isError) return <ErrorState error={q.error} onRetry={q.refetch} />;
  if (q.isLoading || !q.data) return <Skeleton height={400} />;
  const d = q.data;
  const s = d.summary;
  const pctFmt = (v) => (v === null || v === undefined ? "—" : fmt.number(v / 100, { style: "percent", maximumFractionDigits: 0 }));

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        <KpiTile icon={Award} label={t("kpi.recognitions")} value={fmt.number(s.recognitions.value)} metric={s.recognitions} tone="purple" sub={s.received_per_member !== null ? t("kpi.per_member", { value: fmt.number(s.received_per_member, { maximumFractionDigits: 1 }) }) : undefined} />
        <KpiTile icon={Users} label={t("participation.rate")} value={pctFmt(s.participation_rate)} progress={s.participation_rate} metric={s.participants} tone="blue" sub={t("kpi.of_members", { participants: fmt.number(s.participants.value), headcount: fmt.number(s.members) })} />
        <KpiTile icon={Heart} label={t("participation.recipient_rate")} value={pctFmt(s.recipient_rate)} progress={s.recipient_rate} tone="pink" />
        <KpiTile icon={Send} label={t("participation.sender_rate")} value={pctFmt(s.sender_rate)} progress={s.sender_rate} tone="teal" />
        <KpiTile icon={Coins} label={t("kpi.points_awarded")} value={fmt.number(s.points.value)} metric={s.points} tone="green" />
        <KpiTile icon={UserX} label={t("participation.unrecognised")} value={fmt.number(s.unrecognised)} tone={s.unrecognised ? "red" : "green"} sub={t("team.unrecognised_sub")} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <ChartCard title={t("volume_title")} icon={CalendarClock} className="lg:col-span-2">
          <TrendChart rows={d.series} granularity={d.scope?.granularity} series={[{ key: "recognitions", label: t("series.recognitions"), color: SERIES[0], kind: "bar" }, { key: "unique_recipients", label: t("series.unique_recipients"), color: SERIES[2], kind: "line" }]} />
        </ChartCard>
        <ChartCard title={t("team.flow_title")} subtitle={t("team.flow_subtitle")} icon={Users}>
          <SegmentBar segments={[{ label: t("team.internal"), value: s.internal, color: SERIES[0] }, { label: t("team.inbound"), value: s.inbound, color: SERIES[2] }, { label: t("team.outbound"), value: s.outbound, color: SERIES[1] }]} />
          {d.top_external_supporters.length > 0 && (
            <div className="mt-3">
              <p className="text-[11px] font-semibold text-slate-500 mb-1">{t("team.external_supporters")}</p>
              <RankList rows={d.top_external_supporters.map((r, i) => ({ ...r, rank: i + 1, value: r.count }))} />
            </div>
          )}
        </ChartCard>
      </div>

      <ChartCard title={t("team.members_title")} subtitle={t("team.members_subtitle")} icon={Users}>
        <DataTable
          keyField="id"
          rows={d.members.map((m) => ({ ...m, id: m.user?.id }))}
          initialSort={{ key: "received", dir: "desc" }}
          columns={[
            { key: "name", label: t("table.name"), render: (m) => <PersonCell user={m.user} sub={m.user?.job_title} />, sortValue: (m) => (m.user?.full_name || "").toLowerCase() },
            { key: "received", label: t("table.received"), align: "right", render: (m) => (
              <span>
                {fmt.number(m.received)}
                <span className="block text-[10px] text-slate-400">{t("leaderboards.prev", { value: m.received_previous })}</span>
              </span>
            ) },
            { key: "given", label: t("table.given"), align: "right", render: (m) => (
              <span>
                {fmt.number(m.given)}
                <span className="block text-[10px] text-slate-400">{t("leaderboards.prev", { value: m.given_previous })}</span>
              </span>
            ) },
            { key: "points_received", label: t("table.points"), align: "right", render: (m) => fmt.number(m.points_received) },
            { key: "days_since_received", label: t("table.last_recognised"), align: "right", sortValue: (m) => (m.days_since_received === null ? Number.MAX_SAFE_INTEGER : m.days_since_received), render: (m) => (m.last_received_at ? <span className={`text-xs ${m.days_since_received > 60 ? "text-rose-600 font-medium" : "text-slate-700"}`}>{t("days_ago", { count: m.days_since_received })}</span> : <span className="text-xs font-semibold text-rose-600">{t("never")}</span>) },
            { key: "days_since_given", label: t("table.last_gave"), align: "right", sortValue: (m) => (m.days_since_given === null ? Number.MAX_SAFE_INTEGER : m.days_since_given), render: (m) => (m.last_given_at ? <span className="text-xs text-slate-700">{t("days_ago", { count: m.days_since_given })}</span> : <span className="text-xs text-slate-400">{t("never")}</span>) },
            { key: "streak", label: t("me.streak"), align: "right", render: (m) => (m.streak ? <span className="inline-flex items-center gap-0.5 text-xs text-amber-700"><Flame className="w-3 h-3" aria-hidden="true" />{m.streak}</span> : <span className="text-xs text-slate-300">—</span>) },
          ]}
        />
      </ChartCard>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title={t("type_breakdown_title")} icon={PieIcon}>
          <DonutChart data={d.by_type} height={200} centerLabel={t("series.recognitions")} />
        </ChartCard>
        <ChartCard title={t("team.unrecognised_title")} subtitle={t("team.unrecognised_subtitle")} icon={UserX}>
          {d.unrecognised.length === 0 ? (
            <EmptyState message={t("team.everyone_recognised")} height={140} />
          ) : (
            <ul className="divide-y divide-slate-100">
              {d.unrecognised.map((u) => (
                <li key={u.id} className="flex items-center gap-3 py-2">
                  <div className="flex-1 min-w-0">
                    <PersonCell user={u} sub={u.job_title} />
                  </div>
                  <span className="text-xs text-slate-500">{u.last_received_at ? t("me.last", { when: fmt.relative(u.last_received_at) }) : t("never")}</span>
                  <Link to={`/recognition?recipient=${u.id}`} className="text-xs font-semibold text-purple-700 hover:underline">
                    {t("team.recognise")}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </ChartCard>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------------ */
/* Page                                                                       */
/* ------------------------------------------------------------------------ */

const InsightsPage = () => {
  const { t } = useTranslation("analytics");
  const [searchParams, setSearchParams] = useSearchParams();
  const [preset, setPreset] = useState("90d");
  const teams = useQuery({ queryKey: ["analytics", "teams-mine"], queryFn: analyticsService.getAnalysableTeams, staleTime: 5 * 60 * 1000 });
  const teamList = useMemo(() => teams.data?.teams || [], [teams.data]);
  const requested = searchParams.get("team");
  const view = requested && teamList.some((x) => x.id === requested) ? requested : "me";

  const choose = (id) => {
    const next = new URLSearchParams(searchParams);
    if (id === "me") next.delete("team");
    else next.set("team", id);
    setSearchParams(next);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8 pt-3 sm:pt-8 space-y-4">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-slate-800">{t("me.title")}</h1>
            <p className="text-sm text-slate-600 mt-0.5">{view === "me" ? t("me.subtitle") : t("team.subtitle")}</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {teamList.length > 0 && (
              <Segmented ariaLabel={t("me.view_label")} size="md" value={view} onChange={choose} options={[{ value: "me", label: t("me.view_me") }, ...teamList.map((x) => ({ value: x.id, label: x.name }))]} />
            )}
            <Segmented ariaLabel={t("me.period_label")} size="md" value={preset} onChange={setPreset} options={PRESETS.map((p) => ({ value: p, label: t(`presets.${p}`) }))} />
          </div>
        </motion.div>
        {view === "me" ? <MyInsights preset={preset} /> : <TeamInsights teamId={view} preset={preset} />}
      </div>
    </div>
  );
};

export default InsightsPage;
